import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useIntersectionObserver } from '@miro-site/core';
import { IMAGE_FORMATS } from './imageFormats';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var AbsoluteContainer = styled.div.withConfig({
  displayName: "LazyImage__AbsoluteContainer",
  componentId: "sc-20ost4-0"
})(["width:100%;height:100%;top:0;left:0;position:absolute;"]);
var RelativeContainer = styled.div.withConfig({
  displayName: "LazyImage__RelativeContainer",
  componentId: "sc-20ost4-1"
})(["position:relative;"]);
var Image = styled.img.withConfig({
  displayName: "LazyImage__Image",
  componentId: "sc-20ost4-2"
})(["width:100%;height:auto;z-index:", ";opacity:", ";transition:opacity ", " ease-in-out;filter:", ";"], function (_ref) {
  var visible = _ref.visible;
  return visible ? '1' : '0';
}, function (_ref2) {
  var visible = _ref2.visible;
  return visible ? '1' : '0';
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.animation.transitionDuration;
}, function (_ref4) {
  var lowRes = _ref4.lowRes;
  return lowRes && 'blur(6px)';
});
var AbsoluteImage = styled(Image).withConfig({
  displayName: "LazyImage__AbsoluteImage",
  componentId: "sc-20ost4-3"
})(["position:absolute;top:0;left:0;width:100%;height:100%;"]);

function processLazyload(element) {
  var sources = element.querySelectorAll('source');
  var images = element.querySelectorAll('img');
  sources.forEach(function (s) {
    if (s.dataset.srcset) {
      s.srcset = s.dataset.srcset;
    }
  });
  images.forEach(function (i) {
    if (!i.src && i.dataset.src) {
      i.src = i.dataset.src;
    }
  });
}

var Picture = function Picture(_ref5) {
  var _ref5$pictures = _ref5.pictures,
      pictures = _ref5$pictures === void 0 ? {} : _ref5$pictures,
      name = _ref5.name,
      loaded = _ref5.loaded,
      _onLoad = _ref5.onLoad,
      lowRes = _ref5.lowRes,
      _ref5$lazyload = _ref5.lazyload,
      lazyload = _ref5$lazyload === void 0 ? true : _ref5$lazyload,
      _ref5$detectIntersect = _ref5.detectIntersection,
      detectIntersection = _ref5$detectIntersect === void 0 ? true : _ref5$detectIntersect,
      rootRef = _ref5.rootRef,
      width = _ref5.width,
      height = _ref5.height;
  var container = useIntersectionObserver(rootRef, {
    rootMargin: '300px 0px 300px 0px',
    freezeOnceVisible: true
  });
  var isVisible = !!(container !== null && container !== void 0 && container.isIntersecting);
  useEffect(function () {
    if (!lazyload || !(rootRef !== null && rootRef !== void 0 && rootRef.current)) {
      return;
    }

    var isIntersectionObserverSupported = 'IntersectionObserver' in window && 'IntersectionObserverEntry' in window;

    if (!detectIntersection || !isIntersectionObserverSupported || isVisible) {
      processLazyload(rootRef.current);
    }
  }, [isVisible, lazyload, rootRef, processLazyload, detectIntersection]);
  var ImageComponent = width && height ? AbsoluteImage : Image;
  var original = ['jpg', 'png', 'svg', '*'].find(function (t) {
    return !!pictures[t];
  });
  return /*#__PURE__*/_jsxs("picture", {
    children: [pictures.webp && /*#__PURE__*/_jsx("source", {
      "data-srcset": lazyload ? pictures.webp : undefined,
      srcSet: !lazyload ? pictures.webp : undefined,
      type: IMAGE_FORMATS.webp
    }), original && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("source", {
        "data-srcset": lazyload ? pictures[original] : undefined,
        srcSet: !lazyload ? pictures[original] : undefined,
        type: IMAGE_FORMATS[original]
      }), /*#__PURE__*/_jsx(ImageComponent, {
        onLoad: function onLoad() {
          document.dispatchEvent(new Event('lazyloaded'));

          if (_onLoad) {
            _onLoad();
          }
        },
        "data-src": lazyload ? pictures[original] : undefined,
        src: !lazyload ? pictures[original] : undefined,
        width: width,
        height: height,
        alt: name,
        visible: loaded,
        lowRes: lowRes
      })]
    })]
  });
};

var LazyImage = function LazyImage(_ref6) {
  var placeholder = _ref6.placeholder,
      original = _ref6.original,
      name = _ref6.name,
      className = _ref6.className,
      lazyload = _ref6.lazyload,
      detectIntersection = _ref6.detectIntersection,
      rootRef = _ref6.rootRef,
      width = _ref6.width,
      height = _ref6.height;

  var _useState = useState(false),
      loaded = _useState[0],
      setLoaded = _useState[1];

  var WrapperComponent = width && height ? AbsoluteContainer : RelativeContainer;

  var _useState2 = useState(null),
      containerRef = _useState2[0],
      setContainerRef = _useState2[1];

  var wrapperRef = useRef(null);
  useEffect(function () {
    if (rootRef !== null && rootRef !== void 0 && rootRef.current) {
      setContainerRef(rootRef);
      return;
    }

    if (wrapperRef !== null && wrapperRef !== void 0 && wrapperRef.current) {
      setContainerRef(wrapperRef);
      return;
    }
  }, [rootRef, wrapperRef]);
  return /*#__PURE__*/_jsx(WrapperComponent, {
    className: className,
    ref: wrapperRef,
    children: placeholder ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Picture, {
        rootRef: containerRef,
        pictures: original,
        name: name,
        lazyload: lazyload,
        detectIntersection: detectIntersection,
        loaded: loaded,
        onLoad: function onLoad() {
          return setLoaded(true);
        },
        width: width,
        height: height
      }), /*#__PURE__*/_jsx(Picture, {
        rootRef: containerRef,
        pictures: placeholder,
        width: width,
        height: height,
        lowRes: true,
        lazyload: false,
        detectIntersection: detectIntersection,
        name: name,
        loaded: !loaded
      })]
    }) : /*#__PURE__*/_jsx(Picture, {
      rootRef: containerRef,
      pictures: original,
      name: name,
      lazyload: lazyload,
      detectIntersection: detectIntersection,
      loaded: loaded,
      onLoad: function onLoad() {
        return setLoaded(true);
      },
      width: width,
      height: height
    })
  });
};

export default LazyImage;